import { createFileRoute } from '@tanstack/react-router';
import BadgeIcon from '@mui/icons-material/Badge';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import i18n from 'i18next';
import Backend from 'i18next-http-backend';
import request from '@nineeng/request';
import { useEffect, useState } from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';
import { CustomerLogo, MagicCodeField, TaskDialog } from '@repo/components';
import { Task, UserOverview } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { appTheme } from '@repo/theme';
import { ThemeProvider } from '@mui/material/styles';
import { CheckCircleIcon, LoaderCircle, XCircleIcon } from 'lucide-react';
import TermsAndConditions from 'src/components/app/TermsAndConditions';

i18n
	.use(Backend)
	.use(initReactI18next)
	.init({
		backend: {
			loadPath: '/api/translations/{{lng}}/{{ns}}',
		},
		ns: ['APP'],
		defaultNS: 'APP',
		lng: 'en',
		fallbackLng: 'en',
	});

interface SearchParams {
	deviceId?: string;
	deviceToken?: string;
}

export const Route = createFileRoute('/app/onboarding')({
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		deviceId: search.deviceId as string | undefined,
		deviceToken: search.deviceToken as string | undefined,
	}),
	component: OnboardingPage,
});

const backgrounds = {
	pending: 'white',
	checkin: 'linear-gradient(145deg, rgba(46, 204, 113, 1) 0%, rgba(22, 160, 133, 1) 100%)',
	error: 'linear-gradient(145deg, rgba(211, 84, 0, 1) 0%, rgba(192, 57, 43, 1) 100%)',
};

const taskStyles = {
	done: { backgroundColor: 'success.light', opacity: 1 },
	failed: { backgroundColor: 'error.light', opacity: 1 },
	pending: { backgroundColor: 'warning.light', opacity: 1 },
};

interface CheckinResult {
	hasAccess: boolean;
	result: 'checkin' | 'checkout' | 'warning' | 'error';
	direction: 'in' | 'out';
	reason: string;
	eventsAvailable: boolean;
	event: Event;
}

type CheckinStatus = 'pending' | 'checkin' | 'checkout' | 'warning' | 'error';

type Screen = 'magic-code' | 'accept-terms' | 'task-overview' | 'show-checkin-status' | 'finished';

const MagicCodeScreen = ({ onChange }: { onChange: (_code: string) => void }) => (
	<div className="w-[500px]">
		<Typography>Please enter your invite code to get started:</Typography>
		<MagicCodeField placeholder="AAAA-BBBB-CCCC-DDDD" onChange={onChange} />
	</div>
);

function OnboardingPage() {
	const { deviceId, deviceToken } = Route.useSearch();
	const auth = `deviceId=${deviceId}&deviceToken=${deviceToken}`;

	const [magicCode, setMagicCode] = useState<string | undefined>();
	const [screen, setScreen] = useState<Screen>('magic-code');
	const [checkinStatus, setCheckinStatus] = useState<CheckinStatus>('pending');
	const [overview, setOverview] = useState<UserOverview | undefined>();
	const [activeTask, setActiveTask] = useState<Task | undefined>();
	const { t } = useTranslation();

	const clearOverview = () => {
		setOverview(undefined);
		setMagicCode(undefined);
		setActiveTask(undefined);
		setCheckinStatus('pending');
		setScreen('magic-code');
	};

	const handleCheckin = async (token: string, eventId?: string) => {
		try {
			const query = eventId ? `&eventId=${eventId}` : '';
			const { body } = await request.get<CheckinResult>(
				`/api/devices/${deviceId}/checkin/${token}?${auth}${query}`,
				{},
			);
			const { result } = body;
			setCheckinStatus(result);
		} catch (e) {
			console.error('Checkin error:', e);
			setCheckinStatus('error');
		} finally {
			setTimeout(() => clearOverview(), 7000);
		}
	};

	const handleGetUserOverview = async (code: string) => {
		if (!code) return;

		try {
			const { body: userOverview } = await request.get<UserOverview>(
				`/api/devices/${deviceId}/user/overview?${auth}&magicCode=${code}`,
			);
			setOverview(userOverview);
			if (userOverview?.user?.language) {
				i18n.changeLanguage(userOverview.user.language);
			}
			if (userOverview?.openTasks?.length === 0) {
				if (userOverview.autoCheckin && !userOverview?.markedForReview) {
					setScreen('show-checkin-status');
					setCheckinStatus('pending');
					await handleCheckin(userOverview.accessCode);
				} else {
					setScreen('finished');
				}
			}
		} catch (e) {
			console.error('User overview error:', e);
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Invalid magic code, please try again',
			});
			setScreen('magic-code');
		}
	};

	const handleMagicCodeChange = async (code: string) => {
		if (!code) return;
		setMagicCode(code);
		setScreen('accept-terms');
		await handleGetUserOverview(code);
	};

	const handleTaskClose = async () => {
		if (magicCode) {
			await handleGetUserOverview(magicCode);
		}
		setActiveTask(undefined);
	};

	useEffect(() => {
		window.parent.postMessage({ isReady: true }, '*');
	}, []);

	return (
		<div className="!bg-white">
			<ThemeProvider theme={appTheme}>
				{overview && (
					<AppBar position="static">
						<Toolbar sx={{ height: 64 }}>
							<IconButton size="large" edge="start" color="inherit" onClick={clearOverview}>
								<CloseIcon />
							</IconButton>
							<div className="flex-grow" />
							<IconButton
								size="large"
								edge="start"
								color="inherit"
								onClick={() => {
									if (!magicCode) return;
									handleGetUserOverview(magicCode);
								}}
							>
								<RefreshIcon />
							</IconButton>
						</Toolbar>
					</AppBar>
				)}

				<div className="absolute top-16 bottom-0 left-0 right-0 flex flex-col justify-start items-center overflow-y-auto">
					<CustomerLogo />

					{screen === 'magic-code' && <MagicCodeScreen onChange={handleMagicCodeChange} />}
					{screen === 'accept-terms' && <TermsAndConditions onAccept={() => setScreen('task-overview')} />}
					{screen === 'task-overview' && overview && (
						<div className="w-[500px] max-w-[90%]">
							<Typography variant="h6" className="my-2">
								{t('LBL_HI')} {overview?.user?.firstName}
							</Typography>
							<Typography sx={{ marginBottom: 2 }}>
								{t('LBL_WELCOME_TO')} <b>{overview?.customerName}</b>.
							</Typography>
							{overview.markedForReview && (
								<div className="mb-2">
									<Alert severity="warning">{t('LBL_ACCOUNT_IN_REVIEW')}</Alert>
								</div>
							)}

							{!!overview?.openTasks?.length && (
								<div className="mb-4">
									<div className="mb-2">
										{overview.showAccessCode ? (
											<Typography>{t('LBL_STAY_VALIDATED')}</Typography>
										) : (
											<Typography>{t('LBL_GET_VALIDATED')}</Typography>
										)}
									</div>

									{overview.openTasks?.map((task) => (
										<Card elevation={3} className="mb-2" key={task.id} variant="outlined" color="secondary">
											<List disablePadding>
												<ListItemButton
													onClick={() => setActiveTask(task)}
													disabled={task.disabled || task.scope === 'adminOnly'}
													sx={taskStyles[task.status]}
												>
													<ListItemIcon>
														<BadgeIcon />
													</ListItemIcon>
													<ListItemText
														primary={task.name}
														secondary={
															task.scope === 'adminOnly'
																? t('LBL_ADMIN_TASK')
																: t('LBL_DURATION').replace('%', String(task.estMinutes || 5))
														}
													/>
												</ListItemButton>
											</List>
										</Card>
									))}
								</div>
							)}

							<TaskDialog
								task={activeTask}
								onClose={handleTaskClose}
								auth={auth}
								userId={overview ? overview?.user?.id : ''}
							/>
						</div>
					)}

					{screen === 'finished' && (
						<div className="w-[500px] max-w-[90%]">
							<div className="mt-4">
								{overview?.markedForReview && (
									<div className="mb-2">
										<Alert severity="warning">{t('LBL_ACCOUNT_IN_REVIEW')}</Alert>
									</div>
								)}
								{!overview?.markedForReview && <Typography className="mb-2">{t('LBL_ALL_DONE')}</Typography>}
								<Button variant="outlined" className="mb-2" fullWidth onClick={clearOverview}>
									{t('BTN_RETURN')}
								</Button>
							</div>
						</div>
					)}

					{screen === 'show-checkin-status' && (
						<div
							className="w-full flex flex-col items-center justify-center h-[100vh] text-center"
							style={{
								background: backgrounds[checkinStatus] || backgrounds.error,
								position: 'fixed',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
						>
							{checkinStatus === 'pending' && (
								<>
									<LoaderCircle className="mb-2 animate-spin" size={128} />
									<Typography className="mb-2 font-bold text-4xl">{t('LBL_CHECKIN_STATUS')}</Typography>
								</>
							)}
							{checkinStatus === 'checkin' && (
								<>
									<CheckCircleIcon className="mb-4 text-white" size={128} />
									<Typography className="mb-2 font-bold text-white text-4xl">{t('LBL_CHECKIN_SUCCESS')}</Typography>
								</>
							)}
							{!['checkin', 'pending'].includes(checkinStatus) && (
								<>
									<XCircleIcon className="mb-4 text-white" size={128} />
									<Typography className="mb-2 font-bold text-white text-4xl">{t('LBL_CHECKIN_ERROR')}</Typography>
								</>
							)}
						</div>
					)}
				</div>
			</ThemeProvider>
		</div>
	);
}
